/**
 * What has actually been changed, derived rather than recorded.
 *
 * Every panel could keep its own "is this touched" flag, and every history entry
 * could carry a label written at the call site. Both drift. Comparing recipes
 * against the default — and against each other — means the edit list and the
 * history captions are always exactly true, including for changes that arrive
 * from a preset, a synced photo or an accepted proposal.
 */

import { HSL_BANDS, defaultRecipe, isNeutralCurve, type Recipe } from './recipe.ts';
import {
  OPERATIONS_BY_ID,
  SCALAR_OPERATIONS,
  type OperationGroup,
  readPath,
  writePath,
} from './registry.ts';
import { describeCurve } from './curve.ts';
import { formatScalar } from './describe.ts';

export interface ActiveControl {
  /** Registry id where one exists, otherwise a synthetic id for the reset. */
  id: string;
  label: string;
  group: OperationGroup;
  /** The current value, formatted the way the panel shows it. */
  value: string;
  /** Which panel holds it, so the list can jump there. */
  panel: string;
}

const PANEL_OF_GROUP: Record<OperationGroup, string> = {
  light: 'light',
  color: 'color',
  subject: 'masks',
  composition: 'geometry',
  look: 'effects',
};

/** Registry ids that live in a panel other than their semantic group's. */
const PANEL_OVERRIDES: Record<string, string> = {
  texture: 'effects',
  clarity: 'effects',
  dehaze: 'effects',
  sharpenAmount: 'detail',
  sharpenRadius: 'detail',
  sharpenDetail: 'detail',
  sharpenMasking: 'detail',
  noiseLuminance: 'detail',
  noiseLuminanceDetail: 'detail',
  noiseColor: 'detail',
  rotate: 'geometry',
};

function panelFor(id: string, group: OperationGroup): string {
  return PANEL_OVERRIDES[id] ?? PANEL_OF_GROUP[group];
}

/**
 * Every control that differs from the default, in a stable order.
 *
 * `asShot` supplies the camera's own white balance so an untouched temperature
 * is not reported as a change, and a changed one reads against the right
 * starting point.
 */
export function activeControls(
  recipe: Recipe,
  asShot?: { kelvin: number; tint: number },
): ActiveControl[] {
  const out: ActiveControl[] = [];
  const base = defaultRecipe();

  if (recipe.temperature !== null && (!asShot || Math.round(recipe.temperature) !== Math.round(asShot.kelvin))) {
    out.push({
      id: 'temperature',
      label: 'Temperature',
      group: 'color',
      value: `${Math.round(recipe.temperature)}K`,
      panel: 'color',
    });
  }
  if (recipe.tint !== null && (!asShot || Math.round(recipe.tint) !== Math.round(asShot.tint))) {
    out.push({
      id: 'tint',
      label: 'Tint',
      group: 'color',
      value: `${recipe.tint > 0 ? '+' : ''}${Math.round(recipe.tint)}`,
      panel: 'color',
    });
  }

  for (const spec of SCALAR_OPERATIONS) {
    if (spec.id === 'temperature' || spec.id === 'tint') continue;
    const current = readPath(recipe, spec.path);
    const original = readPath(base, spec.path);
    if (typeof current !== 'number' || typeof original !== 'number') continue;
    // Compared as displayed, not against the registry's nominal step. A panel
    // may offer a finer slider than the registry declares — exposure is stepped
    // at 0.05 here and 0.01 in the rail — and a move the photographer can see in
    // the picture must never be reported as no change. Formatting also absorbs
    // float dust, which is all the old step/2 threshold was really guarding.
    const shown = formatScalar(spec, current);
    if (shown === formatScalar(spec, original)) continue;
    out.push({
      id: spec.id,
      label: spec.label,
      group: spec.group,
      value: shown,
      panel: panelFor(spec.id, spec.group),
    });
  }

  for (const channel of ['rgb', 'red', 'green', 'blue'] as const) {
    if (isNeutralCurve(recipe.curves[channel])) continue;
    out.push({
      id: `curve.${channel}`,
      label: channel === 'rgb' ? 'Tone curve' : `${channel[0].toUpperCase()}${channel.slice(1)} curve`,
      group: channel === 'rgb' ? 'light' : 'color',
      value: describeCurve(recipe.curves[channel]),
      panel: 'curve',
    });
  }

  if (recipe.crop) {
    const percent = Math.round(recipe.crop.width * recipe.crop.height * 100);
    out.push({ id: 'crop', label: 'Crop', group: 'composition', value: `${percent}% kept`, panel: 'geometry' });
  }
  if (recipe.orientation !== 0) {
    out.push({
      id: 'orientation',
      label: 'Rotation',
      group: 'composition',
      value: `${recipe.orientation * 90}°`,
      panel: 'geometry',
    });
  }
  if (recipe.flipHorizontal || recipe.flipVertical) {
    out.push({
      id: 'flip',
      label: 'Flip',
      group: 'composition',
      value: [recipe.flipHorizontal ? 'horizontal' : '', recipe.flipVertical ? 'vertical' : '']
        .filter(Boolean)
        .join(' + '),
      panel: 'geometry',
    });
  }

  recipe.masks.forEach((mask, index) => {
    const moved = Object.entries(mask.adjustments).filter(([, value]) => value !== 0).length;
    out.push({
      id: `mask.${index}`,
      label: mask.name,
      group: 'subject',
      value: moved === 0 ? 'no adjustments' : `${moved} adjustment${moved === 1 ? '' : 's'}`,
      panel: 'masks',
    });
  });

  return out;
}

/** Returns a copy of `recipe` with one entry from `activeControls` put back. */
export function resetControl(recipe: Recipe, id: string): Recipe {
  const next = structuredClone(recipe);
  const base = defaultRecipe();

  if (id === 'temperature') {
    next.temperature = null;
    return next;
  }
  if (id === 'tint') {
    next.tint = null;
    return next;
  }
  if (id === 'crop') {
    next.crop = null;
    return next;
  }
  if (id === 'orientation') {
    next.orientation = 0;
    return next;
  }
  if (id === 'flip') {
    next.flipHorizontal = false;
    next.flipVertical = false;
    return next;
  }
  if (id.startsWith('curve.')) {
    const channel = id.slice('curve.'.length) as 'rgb' | 'red' | 'green' | 'blue';
    if (channel in next.curves) next.curves[channel] = base.curves[channel];
    return next;
  }
  if (id.startsWith('mask.')) {
    const index = Number.parseInt(id.slice('mask.'.length), 10);
    if (Number.isInteger(index)) next.masks.splice(index, 1);
    return next;
  }

  const spec = OPERATIONS_BY_ID.get(id);
  if (spec && spec.kind === 'scalar') {
    const original = readPath(base, spec.path);
    if (typeof original === 'number') writePath(next, spec.path, original);
  }
  return next;
}

/**
 * A short caption for the step from `previous` to `next`, for the history list.
 * Names up to two controls, then counts the rest.
 */
export function describeStep(previous: Recipe, next: Recipe): string {
  const before = new Map(activeControls(previous).map((entry) => [entry.id, entry.value]));
  const after = new Map(activeControls(next).map((entry) => [entry.id, entry.value]));
  const labels = new Map(activeControls(next).map((entry) => [entry.id, entry.label]));
  for (const entry of activeControls(previous)) labels.set(entry.id, entry.label);

  const changed: string[] = [];
  const cleared: string[] = [];
  for (const [id, value] of after) {
    if (before.get(id) !== value) changed.push(labels.get(id) ?? id);
  }
  for (const id of before.keys()) {
    if (!after.has(id)) cleared.push(labels.get(id) ?? id);
  }

  if (changed.length === 0 && cleared.length === 0) return 'No change';
  if (changed.length === 0) {
    return cleared.length === 1 ? `Cleared ${cleared[0]}` : `Cleared ${cleared.length} controls`;
  }
  if (changed.length === 1) return changed[0];
  if (changed.length === 2) return `${changed[0]} and ${changed[1]}`;
  return `${changed[0]}, ${changed[1]} and ${changed.length - 2} more`;
}

/** True when nothing in the recipe differs from a fresh one. */
export function isUntouched(recipe: Recipe): boolean {
  return activeControls(recipe).length === 0;
}

/** Counts the HSL bands that have been moved, for the colour panel's badge. */
export function movedHslBands(recipe: Recipe): number {
  return HSL_BANDS.filter((band) => {
    const entry = recipe.hsl[band];
    return entry.hue !== 0 || entry.saturation !== 0 || entry.luminance !== 0;
  }).length;
}
